import React, { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  MenuItem,
  Card,
  CardContent,
  Divider,
  IconButton,
  Chip,
  Container,
  useTheme,
  alpha,
} from "@mui/material";
import { usePost } from "../../../../hooks/usePost";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useUpdate } from "../../../../hooks/useUpdate";
import { setRefresh } from "../../../../redux/refreshSlice";
import {
  PlusIcon,
  XIcon,
  PackageIcon,
  DollarSignIcon,
  CalendarIcon,
} from "lucide-react";
import { useGetList } from "../../../../hooks/useGetList";
import { setIsEditMode } from "../../../../redux/itemUnit/productItemUnitSlice";

const ProductItemUnitForm = () => {
  const theme = useTheme();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      name: "",
      sellingPrice: "",
      purchasePrice: "",
      qty: "",
      reOrderQty: "",
      unitInfos: [],
    },
  });

  const { selectedBranch } = useSelector((state) => state.filterItemUnit);

  const { postData, loading } = usePost(`item-unit`);
  const { updateData, loading: updateLoading } = useUpdate(`item-unit`);
  const { id } = useParams();
  const { editItemUnit } = useSelector((state) => state.productItemUnit);
  const { refresh } = useSelector((state) => state.refresh);
  const { data: branchData, loading: branchLoading } =
    useGetList(`branch/options`);

  const {
    fields: unitInfoFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "unitInfos",
  });

  const dispatch = useDispatch();

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      relatedItem: id,
    };

    if (editItemUnit?.isEditMode) {
      await updateData(editItemUnit?.data?._id, payload);
      dispatch(setRefresh(!refresh));
    } else {
      const postPayload = {
        ...payload,
        relatedBranch: selectedBranch,
      };
      await postData(postPayload);
      dispatch(setRefresh(!refresh));
    }
  };

  const handleCancel = () => {
    console.log("cancel");
    dispatch(setIsEditMode({ isEditMode: false, data: {} }));
  };

  useEffect(() => {
    if (!editItemUnit.isEditMode)
      reset({
        name: "",
        sellingPrice: "",
        purchasePrice: "",
        qty: "",
        reOrderQty: "",
        unitInfos: [],
      });

    if (editItemUnit.isEditMode && editItemUnit.data) {
      reset({
        name: editItemUnit?.data?.name,
        sellingPrice: editItemUnit?.data?.sellingPrice?.toString(),
        purchasePrice: editItemUnit?.data?.purchasePrice?.toString(),
        qty: editItemUnit?.data?.qty?.toString(),
        reOrderQty: editItemUnit?.data?.reOrderQty?.toString(),
        unitInfos: editItemUnit?.data?.unitInfos,
      });
    } else {
      reset({
        name: "",
        sellingPrice: "",
        purchasePrice: "",
        qty: "",
        reOrderQty: "",
        unitInfos: [],
      });
    }
  }, [editItemUnit]);

  const handleUnitInfoAddBtn = () => {
    const newUnitInfo = {
      relatedBranch: "",
      purchasePrice: "",
      sellingPrice: "",
      expireDate: "",
      qty: "",
      reOrderQty: "",
    };
    append(newUnitInfo);
  };

  const handleRemoveUnitInfo = (index) => {
    remove(index);
  };

  return (
    <Container sx={{ py: 2 }}>
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          fontWeight="medium"
        >
          Product Item Unit Form
        </Typography>

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Basic Information */}
          <Box sx={{ mb: 3 }}>
            <Controller
              name="name"
              control={control}
              rules={{
                required: "Name is required",
                minLength: {
                  value: 2,
                  message: "Name must be at least 2 characters",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Unit Name"
                  variant="outlined"
                  disabled={loading}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  size="small"
                />
              )}
            />
          </Box>

          {/* Unit Information */}
          {!editItemUnit?.isEditMode && (
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight="medium">
                  Unit Information
                </Typography>
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  onClick={handleUnitInfoAddBtn}
                  startIcon={<PlusIcon size={14} />}
                >
                  Add UnitInfos
                </Button>
              </Box>

              {unitInfoFields.length === 0 ? (
                <Box
                  sx={{
                    p: 3,
                    textAlign: "center",
                    bgcolor: "grey.50",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No unit information added
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click "Add Unit Infos" to add unit-specific details
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {unitInfoFields.map((field, index) => (
                    <Card
                      key={field.id}
                      elevation={1}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        position: "relative",
                        backgroundColor: "background.paper",
                        "&:hover": {
                          boxShadow: 3,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight="medium"
                          color="primary"
                        >
                          Branch {index + 1}
                        </Typography>
                        <IconButton
                          onClick={() => handleRemoveUnitInfo(index)}
                          size="small"
                          sx={{
                            color: "text.secondary",
                            "&:hover": {
                              color: "error.main",
                              backgroundColor: "error.main",
                              color: "white",
                            },
                          }}
                        >
                          <XIcon size={16} />
                        </IconButton>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Controller
                            name={`unitInfos.${index}.relatedBranch`}
                            control={control}
                            rules={{
                              required: "Branch is required",
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                select
                                label="Branch"
                                variant="outlined"
                                fullWidth
                                size="small"
                                disabled={loading}
                                error={
                                  !!errors.unitInfos?.[index]?.relatedBranch
                                }
                                helperText={
                                  errors.unitInfos?.[index]?.relatedBranch
                                    ?.message
                                }
                              >
                                {!branchLoading &&
                                  branchData?.data?.map((option) => (
                                    <MenuItem
                                      key={option._id}
                                      value={option._id}
                                    >
                                      {option.name}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Controller
                            name={`unitInfos.${index}.purchasePrice`}
                            control={control}
                            rules={{
                              required: "Purchase price is required",
                              pattern: {
                                value: /^\d*\.?\d{0,2}$/,
                                message: "Please enter a valid price",
                              },
                              min: {
                                value: 0,
                                message: "Price cannot be negative",
                              },
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Purchase Price"
                                variant="outlined"
                                fullWidth
                                type="number"
                                size="small"
                                disabled={loading}
                                inputProps={{ step: "0.01", min: "0" }}
                                error={
                                  !!errors.unitInfos?.[index]?.purchasePrice
                                }
                                helperText={
                                  errors.unitInfos?.[index]?.purchasePrice
                                    ?.message
                                }
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Controller
                            name={`unitInfos.${index}.sellingPrice`}
                            control={control}
                            rules={{
                              required: "Selling price is required",
                              pattern: {
                                value: /^\d*\.?\d{0,2}$/,
                                message: "Please enter a valid price",
                              },
                              min: {
                                value: 0,
                                message: "Price cannot be negative",
                              },
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Selling Price"
                                variant="outlined"
                                fullWidth
                                type="number"
                                size="small"
                                disabled={loading}
                                inputProps={{ step: "0.01", min: "0" }}
                                error={
                                  !!errors.unitInfos?.[index]?.sellingPrice
                                }
                                helperText={
                                  errors.unitInfos?.[index]?.sellingPrice
                                    ?.message
                                }
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Controller
                            name={`unitInfos.${index}.qty`}
                            control={control}
                            rules={{
                              required: "Quantity is required",
                              pattern: {
                                value: /^\d+$/,
                                message: "Please enter a whole number",
                              },
                              min: {
                                value: 0,
                                message: "Quantity cannot be negative",
                              },
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Quantity"
                                variant="outlined"
                                fullWidth
                                type="number"
                                size="small"
                                disabled={loading}
                                inputProps={{ min: "0" }}
                                error={!!errors.unitInfos?.[index]?.qty}
                                helperText={
                                  errors.unitInfos?.[index]?.qty?.message
                                }
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Controller
                            name={`unitInfos.${index}.reOrderQty`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Reorder Qty"
                                variant="outlined"
                                fullWidth
                                type="number"
                                size="small"
                                disabled={loading}
                                inputProps={{ min: "0" }}
                                error={!!errors.unitInfos?.[index]?.reOrderQty}
                                helperText={
                                  errors.unitInfos?.[index]?.reOrderQty?.message
                                }
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Controller
                            name={`unitInfos.${index}.expireDate`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Expire Date"
                                variant="outlined"
                                fullWidth
                                type="date"
                                size="small"
                                disabled={loading}
                                InputLabelProps={{ shrink: true }}
                                error={!!errors.unitInfos?.[index]?.expireDate}
                                helperText={
                                  errors.unitInfos?.[index]?.expireDate?.message
                                }
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || updateLoading}
            >
              {updateLoading
                ? "Updating..."
                : loading
                  ? "Submitting..."
                  : editItemUnit.isEditMode
                    ? "Update"
                    : "Submit"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default ProductItemUnitForm;
